/* eslint-disable react/prop-types */
import React, {
  useRef, useContext, useEffect
} from 'react';
import proptypes from 'prop-types';
const { bool: boolType, string: stringType, number: numberType, shape, array, arrayOf } = proptypes;
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useDataModel } from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { dynamicRecsDataModel } from '../dataModel';
import {
  isLoading,
  getAppId,
  getLoyaltyMembershipInput,
  getDrecsImpressionID,
  getDrecsComponent,
  getDrecsimpressionType,
  getApiName,
  getTntSchema,
  getWrapperClass,
  getImpressionsNameValue,
} from '../utils/helpers';
import DynamicRecs from './DynamicRecs';
import { ProductPodV7Carousel } from '../core/ProductPodV7Carousel';
import '../../styles/thd-recs-containers.scss';

let obj = null;
let isTNT = false;
if (typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
  LIFE_CYCLE_EVENT_BUS.lifeCycle.on('recs.tnt', (data) => {
    isTNT = data?.eventName === 'tnt';
    obj = data?.output;
  });
}
const DealElasticRecs = (props) => {

  const { storeId, storeZip: zipCode, isLocalized, membershipInformation } = useStore();
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const loadedStoreInfo = useRef({ storeId, zipCode });

  const {
    anchorId,
    schemaName,
    showLoading,
    serviceType,
    maxResults,
    requestKey,
    hideSwatches,
    hideATC,
    hideFavorites,
    parent,
    itemIds,
    impressionData,
    browseNValue,
    name,
    recsContext,
    dealsElasticObj
  } = props;

  const {
    customerType: cType, mcvisID
  } = useThdCustomer() || {};

  const {
    pn120,
    exItems,
    fallbackApiName
  } = dealsElasticObj || {};

  const tntSchema = getTntSchema(obj, schemaName);

  const opts = {
    variables: {
      anchorId: pn120,
      storeId,
      key: requestKey,
      zipCode,
      apiName: getApiName(isTNT, tntSchema, schemaName),
      skipInstallServices: true,
      dataSource: schemaName,
      loyaltyMembershipInput: getLoyaltyMembershipInput(membershipInformation),
      serviceType,
      cType,
      mcvisId: mcvisID,
      timeStamp: null,
      maxResults,
      appId: getAppId(channel, isConsumerApp),
      nvalue: browseNValue,
      recsContext: recsContext || null,
      exItems
    },
    skip: !isLocalized,
    ssr: false
  };

  const {
    data, variables, loading, error, refetch
  } = useDataModel('recs', opts);

  useEffect(() => {
    if (itemIds && itemIds?.length > 0) {
      refetch();
    }
  }, [itemIds]);

  if (data && !loading && isLocalized) {
    loadedStoreInfo.current = {
      storeId: variables.storeId,
      zipCode: variables.zipCode,
      mounted: true
    };
  }

  const wrapperClasses = getWrapperClass(schemaName, showLoading, loading);
  
  if (error ||
    (
      data !== undefined && 
      (!data || !data?.recs || (data?.recs?.products?.length || 0) < 3) && 
      !loading
    )) {
    return <DynamicRecs {...props} schemaName={fallbackApiName} />;
  }

  const anchorIdForAnalytics = anchorId?.split(',')[0];
  let impressionName = '';
  if (!loading) {
    impressionName = getImpressionsNameValue(
    false, // isCollection
    false, // showColUpLevel  
    false, // isCategoryTab
    false, // isIRG
    data?.recs?.metadata,
    data?.recs?.products,
    loading
    );
  }

  const definedProps = Object.fromEntries(
    Object.entries(props).filter(([key, value]) => value !== undefined)
  );
    // Common props for all carousels
  const commonCarouselProps = {
    anchorId,
    itemClass: 'fixed-item',
    scheme: schemaName,
    data: data?.recs,
    podResults: data?.recs?.products?.length,
    loading: isLoading(data, loading),
    storeId: loadedStoreInfo.current.storeId,
    membershipInformation,
    zipCode: loadedStoreInfo.current.zipCode,
    hideSwatches: hideSwatches ?? DealElasticRecs.defaultProps.hideSwatches,
    hideATC: hideATC ?? DealElasticRecs.defaultProps.hideATC,
    hideFavorites: hideFavorites ?? DealElasticRecs.defaultProps.hideFavorites,
    parent: parent || `recs-product-pod-carousel-${schemaName}`,
    telemetryKey: impressionName,
    analyticsAnchorProductSku: anchorIdForAnalytics,
    ...definedProps
  };

  const createCarouselComponent = () => {
      return (
        <ProductPodV7Carousel
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...commonCarouselProps}
        />
      );
  };

  const recsCarouselComponent = createCarouselComponent();

  return (
    <ImpressionProvider
      data={{
        id: getDrecsImpressionID(impressionData),
        component: getDrecsComponent(impressionData),
        name: impressionName,
        type: getDrecsimpressionType(impressionData),
        recType: schemaName || '',
        position: '',
        ...(name ? { sectionSlotID: name } : {}),
      }}
    >
      <div
        id={schemaName}
        className={wrapperClasses}
        data-type="container"
        data-component={`DynamicRecs-${schemaName}`}
      >
        <meta data-prop="name" content={schemaName} />
        {recsCarouselComponent}
      </div>
    </ImpressionProvider>
  );
};

DealElasticRecs.displayName = 'DealElasticRecsComponent';

DealElasticRecs.propTypes = {
  anchorId: stringType.isRequired,
  schemaName: stringType.isRequired,
  hideFavorites: boolType,
  maxItems: numberType,
  serviceType: stringType,
  showLoading: boolType,
  maxResults: stringType,
  browseNValue: stringType,
  requestKey: stringType,
  hideATC: boolType,
  checkMinimumProducts: boolType,
  itemIds: stringType,
  impressionData: shape({
    id: stringType,
    component: stringType,
    type: stringType
  }),
  name: stringType,
  miniumumProductCount: numberType,
  dealsElasticObj: shape({
    exItems: stringType,
    pn120: stringType.isRequired,
    fallbackApiName: stringType
  })
};

DealElasticRecs.defaultProps = {
  hideFavorites: true,
  maxItems: 16,
  serviceType: null,
  maxResults: null,
  browseNValue: null,
  showLoading: true,
  hideATC: false,
  checkMinimumProducts: false,
  requestKey: 'aGAQFG4j6QtVTSWqujfFYWeIU6BR5Mee',
  itemIds: '',
  impressionData: {
    id: null,
    component: null,
    type: null
  },
  name: '',
  miniumumProductCount: 6,
  dealsElasticObj: {
    exItems: '',
    fallbackApiName: ''
  }
};

DealElasticRecs.dataModel = dynamicRecsDataModel;

export default DealElasticRecs;
